
import React from 'react';
import { Card } from "@/components/ui/card";
import { Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import type { PerformanceData } from './types';

interface TimeAnalysisChartProps {
  performanceData: PerformanceData[];
}

export const TimeAnalysisChart: React.FC<TimeAnalysisChartProps> = ({ performanceData }) => {
  const timeData = React.useMemo(() => {
    const timeRanges = [
      { range: '0-30s', count: 0, color: '#4ADE80' },  // Fast - green
      { range: '30-60s', count: 0, color: '#22D3EE' }, // Medium-fast - cyan
      { range: '60-90s', count: 0, color: '#F59E0B' }, // Medium - amber
      { range: '90-120s', count: 0, color: '#FB923C' }, // Medium-slow - orange
      { range: '120s+', count: 0, color: '#EF4444' }   // Slow - red
    ];

    performanceData.forEach(data => {
      const avgTime = (data.total_questions * 2); // Example calculation, adjust based on actual data
      if (avgTime <= 30) timeRanges[0].count++;
      else if (avgTime <= 60) timeRanges[1].count++;
      else if (avgTime <= 90) timeRanges[2].count++;
      else if (avgTime <= 120) timeRanges[3].count++;
      else timeRanges[4].count++;
    });

    return timeRanges;
  }, [performanceData]);

  return (
    <Card className="p-6 bg-gradient-to-br from-cyan-50 to-white border border-slate-100 shadow-sm">
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 bg-clip-text text-transparent bg-gradient-to-r from-cyan-700 to-blue-500">
        <Clock className="w-5 h-5 text-cyan-500" />
        Time Taken Per Question
      </h2>
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={timeData} 
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <defs>
              {timeData.map((entry, index) => (
                <linearGradient
                  key={`gradient-${index}`}
                  id={`colorGradient-${index}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={entry.color} stopOpacity={0.8} />
                  <stop offset="100%" stopColor={entry.color} stopOpacity={0.3} />
                </linearGradient>
              ))}
              <filter id="shadow-bars" height="130%">
                <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#0284C7" floodOpacity="0.2"/>
              </filter>
            </defs>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
            <XAxis 
              dataKey="range" 
              label={{ 
                value: 'Time Range', 
                position: 'bottom',
                style: { fill: '#6B7280', fontWeight: 500 }
              }}
              tick={{ fill: '#6B7280' }}
              axisLine={{ stroke: '#E5E7EB' }}
            />
            <YAxis 
              label={{ 
                value: 'Number of Questions', 
                angle: -90, 
                position: 'insideLeft',
                style: { fill: '#6B7280', fontWeight: 500 }
              }}
              tick={{ fill: '#6B7280' }}
              axisLine={{ stroke: '#E5E7EB' }}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white', 
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                border: '1px solid #E5E7EB'
              }}
              formatter={(value: number) => [`${value}`, 'Questions']}
              cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
            />
            <Bar 
              dataKey="count" 
              radius={[8, 8, 0, 0]}
              filter="url(#shadow-bars)"
            >
              {timeData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={`url(#colorGradient-${index})`} />
              ))}
              <LabelList dataKey="count" position="top" fill="#6B7280" style={{ fontWeight: 'bold' }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-5 gap-2">
        {timeData.map((range, index) => (
          <div key={index} className="flex flex-col items-center">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: range.color }}
            ></div>
            <span className="text-xs text-gray-500">{range.range}</span>
          </div>
        ))}
      </div>
    </Card>
  );
};
